import React, { useState } from 'react';

const columns = [
  { key: 'symbol', label: 'Asset', tooltip: "Symbole de l'actif (ticker)" },
  { key: 'price', label: 'Prix', tooltip: "Prix actuel de l'actif" },
  { key: 'perf24h', label: '24h', tooltip: "Performance sur 24h (%)" },
  { key: 'perf7d', label: '7j', tooltip: "Performance sur 7 jours (%)" },
  { key: 'volume', label: 'Volume', tooltip: "Volume échangé (24h)" },
  { key: 'volatility', label: 'Volatilité', tooltip: "Volatilité récente (score 0-10)" },
  { key: 'momentumScore', label: 'Momentum', tooltip: "Momentum (score 0-100)" },
  { key: 'trendStrength', label: 'Trend', tooltip: "Force de la tendance (score)" },
  { key: 'riskScore', label: 'Risk', tooltip: "Score de risque (0=faible, 10=élevé)" },
  { key: 'compositeScore', label: 'Score', tooltip: "Score composite global (0-100)" },
];

const PAGE_SIZE = 50;

const formatPrice = (value) => {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return '—';
  }
  const digits = Math.abs(value) >= 100 ? 2 : Math.abs(value) >= 1 ? 4 : 6;
  return value.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: digits });
};

const formatNumber = (value) => {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return '—';
  }
  return value.toLocaleString('fr-FR', { maximumFractionDigits: 2 });
};

const formatCompactNumber = (value) => {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return '—';
  }
  return new Intl.NumberFormat('fr-FR', {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(value);
};

const AssetTable = ({ assets, filters, onSelect, selectedSymbol }) => {
  const [sort, setSort] = useState({ key: 'compositeScore', dir: 'desc' });
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  let filtered = assets;
  if (filters.perf24h) {
    if (filters.perf24h === 'gt5') filtered = filtered.filter(a => a.perf24h > 5);
    if (filters.perf24h === 'gt10') filtered = filtered.filter(a => a.perf24h > 10);
    if (filters.perf24h === 'lt0') filtered = filtered.filter(a => a.perf24h < 0);
  }
  if (filters.momentum) {
    if (filters.momentum === 'high') filtered = filtered.filter(a => a.momentumScore >= 70);
    if (filters.momentum === 'low') filtered = filtered.filter(a => a.momentumScore < 40);
  }
  if (filters.volatility) {
    if (filters.volatility === 'high') filtered = filtered.filter(a => a.volatility >= 7);
    if (filters.volatility === 'low') filtered = filtered.filter(a => a.volatility < 7);
  }
  if (filters.sector) {
    filtered = filtered.filter(a => (a.sector || '').toLowerCase() === filters.sector);
  }

  if (search.trim()) {
    const s = search.trim().toLowerCase();
    filtered = filtered.filter(a =>
      (a.symbol && a.symbol.toLowerCase().includes(s)) ||
      (a.name && a.name.toLowerCase().includes(s))
    );
  }

  const sorted = [...filtered].sort((a, b) => {
    const { key, dir } = sort;
    let va = a[key];
    let vb = b[key];
    if (typeof va === 'string') {
      return dir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
    }
    if (va == null) va = -Infinity;
    if (vb == null) vb = -Infinity;
    return dir === 'asc' ? va - vb : vb - va;
  });

  const handleSort = (key) => {
    setSort(s => s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'desc' });
  };

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const isEmpty = sorted.length === 0;
  const paginated = sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  React.useEffect(() => { setPage(1); }, [search, sort, filters]);

  return (
    <div className="asset-table">
      <div className="asset-table__toolbar">
        <div className="asset-table__summary">
          {sorted.length} actif{sorted.length > 1 ? 's' : ''} après filtres
        </div>
        <input
          className="asset-table__search"
          type="text"
          placeholder="Rechercher un actif..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>
      <div className="asset-table__surface">
        <table>
          <thead>
            <tr>
              {columns.map(col => (
                <th
                  key={col.key}
                  onClick={() => handleSort(col.key)}
                  title={col.tooltip}
                >
                  {col.label}
                  {sort.key === col.key && (sort.dir === 'asc' ? ' ▲' : ' ▼')}
                  <span className="asset-table__hint" title={col.tooltip}>ⓘ</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isEmpty ? (
              <tr>
                <td colSpan={columns.length} className="asset-table__empty">
                  Aucun actif ne correspond à vos filtres ou à votre recherche.
                </td>
              </tr>
            ) : (
              paginated.map(asset => {
                const assetKey = asset.symbol || asset.code;
                const isSelected = assetKey && selectedSymbol && assetKey === selectedSymbol;
                return (
                  <tr
                    key={assetKey}
                    className={isSelected ? 'asset-table__row--selected' : ''}
                    onClick={() => onSelect(asset)}
                  >
                    <td className="asset-table__asset-cell">
                      <div>{asset.symbol}</div>
                      {asset.name ? <span className="asset-table__asset-name">{asset.name}</span> : null}
                    </td>
                    <td>{formatPrice(asset.price)}</td>
                    <td>{formatNumber(asset.perf24h)}</td>
                    <td>{formatNumber(asset.perf7d)}</td>
                    <td>{formatCompactNumber(asset.volume)}</td>
                    <td>
                      {asset.volatility == null ? '—' : (
                        <span style={{
                          display: 'inline-block',
                          minWidth: 34,
                          borderRadius: 999,
                          padding: '4px 10px',
                          background: asset.volatility >= 7 ? '#aa3d33' : asset.volatility >= 4 ? '#8d6c16' : '#1f7a4d',
                          color: '#fff8ef',
                          fontWeight: 700,
                          fontSize: 12,
                        }}>{asset.volatility}</span>
                      )}
                    </td>
                    <td>
                      {asset.momentumScore == null ? '—' : (
                        <span style={{
                          display: 'inline-block',
                          minWidth: 34,
                          borderRadius: 999,
                          padding: '4px 10px',
                          background:
                            asset.momentumScore >= 80 ? '#8fd6b1' :
                            asset.momentumScore >= 60 ? '#1f7a4d' :
                            asset.momentumScore >= 40 ? '#e6be63' :
                            asset.momentumScore >= 20 ? '#d9842b' : '#aa3d33',
                          color: asset.momentumScore >= 80 ? '#17323c' : '#fff8ef',
                          fontWeight: 700,
                          fontSize: 12,
                        }}>{asset.momentumScore}</span>
                      )}
                    </td>
                    <td>{formatNumber(asset.trendStrength)}</td>
                    <td>{formatNumber(asset.riskScore)}</td>
                    <td>
                      {asset.compositeScore == null ? '—' : (
                        <span style={{
                          display: 'inline-block',
                          minWidth: 34,
                          borderRadius: 999,
                          padding: '4px 10px',
                          background:
                            asset.compositeScore >= 80 ? '#8fd6b1' :
                            asset.compositeScore >= 60 ? '#1f7a4d' :
                            asset.compositeScore >= 40 ? '#e6be63' :
                            asset.compositeScore >= 20 ? '#d9842b' : '#aa3d33',
                          color: asset.compositeScore >= 80 ? '#17323c' : '#fff8ef',
                          fontWeight: 800,
                          fontSize: 12,
                        }}>{asset.compositeScore}</span>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      {!isEmpty && totalPages > 1 && (
        <div className="asset-table__pager">
          <button className="asset-table__pager-button" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>Précédent</button>
          <span>Page {page} / {totalPages}</span>
          <button className="asset-table__pager-button" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>Suivant</button>
        </div>
      )}
    </div>
  );
};

export default AssetTable;
